<script setup>
import { computed, reactive, ref } from 'vue'
import {
  Button,
  FormField,
  Input,
  Modal,
  SegmentedControl,
  Select,
  Textarea,
  useToast,
} from '@drama/ui'
import { useI18n } from 'vue-i18n'

import {
  adjustUserCredits,
  updateUserProfile,
  updateUserSubscription,
} from '../api/users.js'
import { useAdminAuth } from '../composables/useAdminAuth.js'
import { isoToLocalDateTime } from '../pages/billing/format.js'

const props = defineProps({
  detail: { type: Object, required: true },
})
const emit = defineEmits(['updated'])

const { t } = useI18n()
const auth = useAdminAuth()
const toast = useToast()
const isAdmin = computed(() => auth.account.value.role === 'admin')
const saving = ref(false)
const profileOpen = ref(false)
const creditsOpen = ref(false)
const subscriptionOpen = ref(false)

const profile = reactive({ nickname: '', avatar_url: '', locale: 'zh', reason: '' })
const credits = reactive({ type: 'grant', amount: '', reason: '' })
const subscription = reactive({ level: 'free', status: 'none', expires_at: '', reason: '' })

const localeOptions = [
  { value: 'zh', label: '中文' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
]
const creditTypeOptions = computed(() => [
  { value: 'grant', label: t('visualGameAdmin.users.actions.credits.grant') },
  { value: 'deduct', label: t('visualGameAdmin.users.actions.credits.deduct') },
])
const subscriptionStatusOptions = computed(() => [
  { value: 'none', label: t('visualGameAdmin.users.subscriptionStatus.none') },
  { value: 'active', label: t('visualGameAdmin.users.subscriptionStatus.active') },
  { value: 'expired', label: t('visualGameAdmin.users.subscriptionStatus.expired') },
  { value: 'canceled', label: t('visualGameAdmin.users.subscriptionStatus.canceled') },
])
const profileValid = computed(() => profile.reason.trim().length >= 3 && profile.locale.length > 0)
const creditsValid = computed(() => {
  const amount = Number(credits.amount)
  return Number.isInteger(amount) && amount > 0 && amount <= 10000000 && credits.reason.trim().length >= 3
})
const subscriptionValid = computed(() => {
  if (subscription.reason.trim().length < 3) return false
  const expiresAt = subscription.expires_at === '' ? null : new Date(subscription.expires_at)
  switch (subscription.status) {
    case 'none':
      return subscription.level === 'free' && expiresAt === null
    case 'active':
      return subscription.level !== 'free' && (expiresAt === null || expiresAt.getTime() > Date.now())
    case 'expired':
      return subscription.level !== 'free' && expiresAt !== null && expiresAt.getTime() <= Date.now()
    case 'canceled':
      return subscription.level !== 'free'
    default:
      throw new Error(`Unhandled subscription status: ${subscription.status}, id: ${props.detail.user.id}`)
  }
})

function openProfile() {
  profile.nickname = props.detail.user.nickname === null ? '' : props.detail.user.nickname
  profile.avatar_url = props.detail.user.avatar_url === null ? '' : props.detail.user.avatar_url
  profile.locale = effectiveProfileLocale(props.detail.user)
  profile.reason = ''
  profileOpen.value = true
}

function openCredits() {
  credits.type = 'grant'
  credits.amount = ''
  credits.reason = ''
  creditsOpen.value = true
}

function openSubscription() {
  subscription.level = props.detail.subscription.level
  subscription.status = props.detail.subscription.status
  subscription.expires_at = isoToLocalDateTime(props.detail.subscription.expires_at)
  subscription.reason = ''
  subscriptionOpen.value = true
}

async function saveProfile() {
  await performMutation(async () => {
    const result = await updateUserProfile(props.detail.user.id, {
      mutation_id: crypto.randomUUID(),
      nickname: emptyToNull(profile.nickname),
      avatar_url: emptyToNull(profile.avatar_url),
      locale: profile.locale,
      reason: profile.reason.trim(),
    })
    profileOpen.value = false
    return result
  }, 'profile')
}

async function saveCredits() {
  await performMutation(async () => {
    const result = await adjustUserCredits(props.detail.user.id, {
      mutation_id: crypto.randomUUID(),
      type: credits.type,
      amount: Number(credits.amount),
      reason: credits.reason.trim(),
    })
    creditsOpen.value = false
    return result
  }, 'credits')
}

async function saveSubscription() {
  await performMutation(async () => {
    const result = await updateUserSubscription(props.detail.user.id, {
      mutation_id: crypto.randomUUID(),
      level: subscription.level.trim(),
      status: subscription.status,
      expires_at: subscription.expires_at === '' ? null : new Date(subscription.expires_at).toISOString(),
      reason: subscription.reason.trim(),
    })
    subscriptionOpen.value = false
    return result
  }, 'subscription')
}

/** @param {() => Promise<Record<string, any>>} action @param {'profile'|'credits'|'subscription'} kind */
async function performMutation(action, kind) {
  saving.value = true
  try {
    const result = await action()
    emit('updated', result.detail)
    toast.success(t(`visualGameAdmin.users.actions.${kind}.success`))
  } catch (error) {
    toast.error(error.message, { duration: 0 })
  } finally {
    saving.value = false
  }
}

/** @param {Record<string, any>} user */
function effectiveProfileLocale(user) {
  if (user.profile_locale !== null) return user.profile_locale
  if (user.account_locale !== null) return user.account_locale
  return 'zh'
}

/** @param {string} value */
function emptyToNull(value) {
  const normalized = value.trim()
  return normalized === '' ? null : normalized
}

/** @param {string|null} value */
</script>

<template>
  <div class="user-actions" data-insp-path="apps/visual-game-admin/src/components/UserManagementActions.vue#triggers">
    <Button variant="secondary" @click="openProfile">
      {{ t('visualGameAdmin.users.actions.profile.trigger') }}
    </Button>
    <template v-if="isAdmin">
      <Button variant="secondary" @click="openCredits">
        {{ t('visualGameAdmin.users.actions.credits.trigger') }}
      </Button>
      <Button variant="secondary" @click="openSubscription">
        {{ t('visualGameAdmin.users.actions.subscription.trigger') }}
      </Button>
    </template>
  </div>

  <Modal v-model:open="profileOpen" :title="t('visualGameAdmin.users.actions.profile.title')" :close-on-backdrop="!saving">
    <form class="user-actions__form" @submit.prevent="saveProfile">
      <FormField :label="t('visualGameAdmin.users.actions.profile.nickname')">
        <Input v-model="profile.nickname" :maxlength="120" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.users.actions.profile.avatar')">
        <Input v-model="profile.avatar_url" type="url" :maxlength="2000" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.users.actions.profile.locale')" required>
        <Select v-model="profile.locale" :options="localeOptions" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.users.actions.reason')" required>
        <Textarea v-model="profile.reason" :maxlength="500" :rows="3" block />
      </FormField>
    </form>
    <template #footer>
      <Button variant="ghost" :disabled="saving" @click="profileOpen = false">{{ t('visualGameAdmin.common.cancel') }}</Button>
      <Button :loading="saving" :disabled="!profileValid" @click="saveProfile">{{ t('visualGameAdmin.common.save') }}</Button>
    </template>
  </Modal>

  <Modal v-model:open="creditsOpen" :title="t('visualGameAdmin.users.actions.credits.title')" :close-on-backdrop="!saving">
    <div class="user-actions__form">
      <p class="user-actions__notice">{{ t('visualGameAdmin.users.actions.credits.notice', { balance: detail.credits.balance }) }}</p>
      <FormField :label="t('visualGameAdmin.users.actions.credits.type')" required>
        <SegmentedControl v-model="credits.type" :options="creditTypeOptions" />
      </FormField>
      <FormField :label="t('visualGameAdmin.users.actions.credits.amount')" required>
        <Input v-model="credits.amount" type="number" min="1" max="10000000" step="1" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.users.actions.reason')" required>
        <Textarea v-model="credits.reason" :maxlength="500" :rows="3" block />
      </FormField>
    </div>
    <template #footer>
      <Button variant="ghost" :disabled="saving" @click="creditsOpen = false">{{ t('visualGameAdmin.common.cancel') }}</Button>
      <Button :variant="credits.type === 'deduct' ? 'danger' : 'primary'" :loading="saving" :disabled="!creditsValid" @click="saveCredits">
        {{ t('visualGameAdmin.users.actions.credits.confirm') }}
      </Button>
    </template>
  </Modal>

  <Modal v-model:open="subscriptionOpen" :title="t('visualGameAdmin.users.actions.subscription.title')" :close-on-backdrop="!saving">
    <div class="user-actions__form">
      <FormField :label="t('visualGameAdmin.users.actions.subscription.level')" required>
        <Input v-model="subscription.level" :maxlength="32" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.users.actions.subscription.status')" required>
        <Select v-model="subscription.status" :options="subscriptionStatusOptions" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.users.actions.subscription.expiresAt')">
        <Input v-model="subscription.expires_at" type="datetime-local" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.users.actions.reason')" required>
        <Textarea v-model="subscription.reason" :maxlength="500" :rows="3" block />
      </FormField>
      <p class="user-actions__notice">{{ t(`visualGameAdmin.users.actions.subscription.rules.${subscription.status}`) }}</p>
    </div>
    <template #footer>
      <Button variant="ghost" :disabled="saving" @click="subscriptionOpen = false">{{ t('visualGameAdmin.common.cancel') }}</Button>
      <Button :loading="saving" :disabled="!subscriptionValid" @click="saveSubscription">{{ t('visualGameAdmin.common.save') }}</Button>
    </template>
  </Modal>
</template>

<style scoped>
.user-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-sm);
}

.user-actions__form {
  display: grid;
  gap: var(--drama-space-lg);
}

.user-actions__notice {
  margin: 0;
  padding: var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}
</style>
